import React from 'react';
import ReactDOM from 'react-dom';
import { X } from 'lucide-react';
import './AlertModal.css';

interface AlertModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  buttonText?: string;
  buttonVariant?: 'primary' | 'danger' | 'secondary' | 'success';
  onClose: () => void;
  onConfirm?: () => void;
  confirmText?: string;
  cancelText?: string;
  isConfirmation?: boolean;
  icon?: React.ReactNode;
}

const AlertModal: React.FC<AlertModalProps> = ({
  isOpen,
  title,
  message,
  buttonText = 'OK',
  buttonVariant = 'primary',
  onClose,
  onConfirm,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  isConfirmation = false,
  icon
}) => {
  if (!isOpen) return null;

  const getButtonStyle = () => {
    switch (buttonVariant) {
      case 'danger':
        return {
          background: 'rgba(239, 68, 68, 0.9)',
          hoverBackground: 'rgba(220, 38, 38, 0.9)'
        };
      case 'secondary':
        return {
          background: 'rgba(107, 114, 128, 0.9)',
          hoverBackground: 'rgba(75, 85, 99, 0.9)'
        };
      case 'success':
        return {
          background: 'rgba(16, 185, 129, 0.9)',
          hoverBackground: 'rgba(5, 150, 105, 0.9)'
        };
      default: // primary
        return {
          background: 'rgba(59, 130, 246, 0.9)',
          hoverBackground: 'rgba(37, 99, 235, 0.9)'
        };
    }
  };

  const buttonStyle = getButtonStyle();

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === 'Escape') {
      onClose();
    }
  };

  return ReactDOM.createPortal(
    <div 
      className="alert-modal-overlay"
      onClick={onClose}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      <div 
        className="alert-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="alert-modal-header">
          {icon && (
            <div className="alert-modal-icon">
              {icon}
            </div>
          )}
          <h3 className="alert-modal-title">{title}</h3>
          <button 
            className="alert-modal-close"
            onClick={onClose}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Message */}
        <p className="alert-modal-message">{message}</p>

        {/* Action */}
        <div className="alert-modal-actions">
          {isConfirmation ? (
            <>
              <button
                onClick={onClose}
                className="alert-modal-btn alert-modal-btn-secondary"
                style={{
                  background: 'rgba(107, 114, 128, 0.9)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(75, 85, 99, 0.9)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(107, 114, 128, 0.9)';
                }}
              >
                {cancelText}
              </button>
              <button
                onClick={onConfirm}
                className="alert-modal-btn"
                style={{
                  background: buttonStyle.background
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = buttonStyle.hoverBackground;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = buttonStyle.background;
                }}
                autoFocus
              >
                {confirmText}
              </button>
            </>
          ) : (
            <button
              onClick={onClose}
              className="alert-modal-btn"
              style={{
                background: buttonStyle.background
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = buttonStyle.hoverBackground;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = buttonStyle.background;
              }}
              autoFocus
            >
              {buttonText}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AlertModal;
